import { PrimaryButton, SecondaryButton } from '@shared/components/buttons';
import { Modal } from '@shared/components/modal';
import type React from 'react';
import type { FieldValues, SubmitHandler, UseFormReturn } from 'react-hook-form';
import { useUnsavedChangesGuard } from '../hooks/use-unsaved-changes-guard';

// The three generics mirror useForm's own, because a resolver that transforms
// (zGoSlice / zGoDate) makes the submitted type differ from the field type:
// vendors/edit is useForm<VendorFormValues, unknown, Vendor> and its onSubmit
// takes a Vendor. Collapsing them here would type that call site wrong.
type FormProperties<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = Omit<React.ComponentProps<'form'>, 'onSubmit' | 'onReset'> & {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  onSubmit: SubmitHandler<TTransformedValues>;
};

/**
 * Form owns the `<form>` element, its submit and reset wiring, and the guard
 * that stops a refresh or a click away from silently throwing out what someone
 * typed.
 *
 * It exists so the guard is not something to remember. A hook called per
 * surface rots the moment a fifth form is added — nothing fails, no test, no
 * type error, it simply has no guard. Here, wiring a new form means using the
 * component you would already reach for, and `no-restricted-syntax` in
 * eslint.config.js keeps raw `<form>` elements out of src/pages so skipping it
 * is a CI failure rather than an oversight.
 *
 * App-only, unlike the field components in @shared/components/forms: the guard
 * is the router's useBlocker, and the marketing site has no router.
 *
 * NOT a guarantee for non-form mutations. Receiving's invoice picker guards on
 * in-flight uploads rather than a dirty form, and no lint rule reaches it; that
 * kind of surface is still a judgment call at each site.
 */
export function Form<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({
  form,
  onSubmit,
  children,
  ...rest
}: FormProperties<TFieldValues, TContext, TTransformedValues>) {
  const blocker = useUnsavedChangesGuard(form);

  return (
    <>
      <form onSubmit={form.handleSubmit(onSubmit)} onReset={() => form.reset()} {...rest}>
        {children}
      </form>

      <Modal
        title="Unsaved changes"
        size="sm"
        visible={blocker.status === 'blocked'}
        onClose={() => blocker.reset?.()}
        footer={
          <>
            <SecondaryButton type="button" onClick={() => blocker.reset?.()}>
              Keep editing
            </SecondaryButton>
            <PrimaryButton type="button" onClick={() => blocker.proceed?.()}>
              Discard changes
            </PrimaryButton>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          You have changes here that have not been saved. Leaving now discards them.
        </p>
      </Modal>
    </>
  );
}
